import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import Swal from 'sweetalert2';
import { API_BASE_URL } from '../config/env';
import { buildCrmSwalBody, fireConfirmDialog } from '../utils/swalConfirm';
import { CwListSearch } from '../components/conversaciones/CwUiParts';
import AmvTablePager from '../components/admin/AmvTablePager';
import UiIcon from '../components/UiIcon';
import '../styles/conversaciones-premium.css';
import '../styles/gestion-derivacion-premium.css';
import '../styles/admin-medicos-visibilidad-premium.css';
import '../styles/farmacia-sucursales-admin.css';
import '../styles/farmacia-poligonos-admin.css';

try {
  delete L.Icon.Default.prototype._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png')
  });
} catch (error) {}

const ENDPOINTS = {
  list: '/api/atencion-bot/poligonos-farmacia',
  create: '/api/atencion-bot/poligonos-farmacia',
  update: (id) => `/api/atencion-bot/poligonos-farmacia/${encodeURIComponent(id)}`,
  delete: (id) => `/api/atencion-bot/poligonos-farmacia/${encodeURIComponent(id)}`
};

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const DEFAULT_CENTER = [-33.02, -68.87];
const DEFAULT_COLORS = ['#2563eb', '#f97316', '#10b981', '#dc2626', '#7c3aed', '#0f766e'];
const POLYGON_FILL_COLOR = '#fde047';

function showToast({ title, text, icon = 'success' }) {
  Swal.fire({
    toast: true,
    position: 'bottom-end',
    icon,
    title,
    text,
    showConfirmButton: false,
    timer: 3200,
    timerProgressBar: true,
    customClass: { popup: 'swal-toast-crm swal-toast-crm--amv' }
  });
}

function normalizeText(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
}

function normalizeColor(value) {
  const raw = String(value || '').trim();
  return /^#(?:[0-9a-fA-F]{6}|[0-9a-fA-F]{3})$/.test(raw) ? raw : '#2563eb';
}

function normalizeVertices(value) {
  if (!Array.isArray(value)) return [];
  return value
    .map((point) => {
      const lat = Number(point?.latitud ?? point?.lat ?? point?.latitude);
      const lng = Number(point?.longitud ?? point?.lng ?? point?.lon ?? point?.longitude);
      return Number.isFinite(lat) && Number.isFinite(lng)
        ? { latitud: Number(lat.toFixed(7)), longitud: Number(lng.toFixed(7)) }
        : null;
    })
    .filter(Boolean);
}

function sortPoligonos(list) {
  return [...list].sort((a, b) => {
    const priorityDelta = Number(b?.prioridad || 0) - Number(a?.prioridad || 0);
    if (priorityDelta !== 0) return priorityDelta;
    return String(a?.nombre || '').localeCompare(String(b?.nombre || ''), 'es', { sensitivity: 'base' });
  });
}

function polygonStatusText(value) {
  return Number(value) === 0 ? 'Pausado' : 'Activo';
}

function polygonBounds(vertices) {
  const points = normalizeVertices(vertices).map((point) => [point.latitud, point.longitud]);
  return points.length ? L.latLngBounds(points) : null;
}

function scaledPoint(point, cosLat) {
  return {
    x: Number(point?.longitud || 0) * cosLat,
    y: Number(point?.latitud || 0)
  };
}

function distanceToSegmentSquared(point, segmentStart, segmentEnd) {
  const avgLat = ((point.latitud + segmentStart.latitud + segmentEnd.latitud) / 3) * (Math.PI / 180);
  const cosLat = Math.max(0.25, Math.cos(avgLat));
  const p = scaledPoint(point, cosLat);
  const a = scaledPoint(segmentStart, cosLat);
  const b = scaledPoint(segmentEnd, cosLat);
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lengthSquared = dx * dx + dy * dy;
  if (!lengthSquared) {
    const px = p.x - a.x;
    const py = p.y - a.y;
    return px * px + py * py;
  }
  const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / lengthSquared));
  const closestX = a.x + t * dx;
  const closestY = a.y + t * dy;
  const px = p.x - closestX;
  const py = p.y - closestY;
  return px * px + py * py;
}

function insertVertexOnClosestSegment(verticesValue, point) {
  const vertices = normalizeVertices(verticesValue);
  const normalizedPoint = normalizeVertices([point])[0];
  if (!normalizedPoint) {
    return { vertices, insertIndex: null, beforeIndex: null, afterIndex: null };
  }
  if (vertices.length < 2) {
    return {
      vertices: [...vertices, normalizedPoint],
      insertIndex: vertices.length,
      beforeIndex: vertices.length ? vertices.length - 1 : null,
      afterIndex: null
    };
  }

  let bestSegment = { distance: Number.POSITIVE_INFINITY, beforeIndex: 0, afterIndex: 1, insertIndex: 1 };
  // Avoid using the automatic closing edge as insertion target; it is often the long line users are trying to fix.
  const segmentCount = vertices.length - 1;
  for (let index = 0; index < segmentCount; index += 1) {
    const afterIndex = (index + 1) % vertices.length;
    const distance = distanceToSegmentSquared(normalizedPoint, vertices[index], vertices[afterIndex]);
    if (distance < bestSegment.distance) {
      bestSegment = {
        distance,
        beforeIndex: index,
        afterIndex,
        insertIndex: afterIndex === 0 ? vertices.length : afterIndex
      };
    }
  }

  const next = [...vertices];
  next.splice(bestSegment.insertIndex, 0, normalizedPoint);
  return {
    vertices: next,
    insertIndex: bestSegment.insertIndex,
    beforeIndex: bestSegment.beforeIndex,
    afterIndex: bestSegment.afterIndex
  };
}

function verticesToPreview(vertices) {
  const list = normalizeVertices(vertices);
  if (!list.length) return 'Sin vértices';
  return `${list.length} vértices`;
}

function emptyFormState() {
  return {
    nombre: '',
    color: DEFAULT_COLORS[0],
    prioridad: 0,
    activo: 1,
    notas: '',
    vertices_json: []
  };
}

function formStateFromRow(row) {
  return {
    nombre: String(row?.nombre || ''),
    color: normalizeColor(row?.color),
    prioridad: Number.isFinite(Number(row?.prioridad)) ? Number(row.prioridad) : 0,
    activo: Number(row?.activo) === 0 ? 0 : 1,
    notas: String(row?.notas || ''),
    vertices_json: normalizeVertices(row?.vertices_json)
  };
}

function payloadFromForm(form) {
  return {
    nombre: String(form?.nombre || '').trim(),
    color: normalizeColor(form?.color),
    prioridad: Number.isFinite(Number(form?.prioridad)) ? Number(form.prioridad) : 0,
    activo: Number(form?.activo) === 0 ? 0 : 1,
    notas: String(form?.notas || '').trim(),
    vertices_json: normalizeVertices(form?.vertices_json)
  };
}

function PolygonMap({ poligonos, selectedId, onSelect, active = true, className = '' }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return undefined;
    mapRef.current = L.map(containerRef.current, {
      center: DEFAULT_CENTER,
      zoom: 11,
      zoomAnimation: false,
      fadeAnimation: false,
      markerZoomAnimation: false
    });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: '' }).addTo(mapRef.current);
    mapRef.current.attributionControl.setPrefix('');
    return () => {
      if (mapRef.current) {
        try { mapRef.current.remove(); } catch (error) {}
        mapRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!mapRef.current) return;
    if (layerRef.current) {
      try { layerRef.current.remove(); } catch (error) {}
      layerRef.current = null;
    }

    const rows = Array.isArray(poligonos) ? poligonos : [];
    if (!rows.length) {
      mapRef.current.setView(DEFAULT_CENTER, 11, { animate: false });
      return;
    }

    const group = L.featureGroup();
    rows.forEach((row) => {
      const vertices = normalizeVertices(row?.vertices_json);
      if (vertices.length < 3) return;
      const isSelected = Number(row?.id) === Number(selectedId);
      const polygon = L.polygon(vertices.map((point) => [point.latitud, point.longitud]), {
        color: normalizeColor(row?.color),
        weight: isSelected ? 4 : 2,
        fillColor: POLYGON_FILL_COLOR,
        fillOpacity: isSelected ? 0.28 : 0.15,
        dashArray: Number(row?.activo) === 0 ? '10 8' : undefined
      });
      polygon.on('click', () => onSelect?.(row?.id));
      polygon.bindPopup(`<strong>${row?.nombre || 'Polígono'}</strong><br/>${Number(row?.activo) !== 0 ? 'Activo' : 'Inactivo'}`);
      polygon.addTo(group);
    });

    group.addTo(mapRef.current);
    layerRef.current = group;

    const selected = rows.find((row) => Number(row?.id) === Number(selectedId));
    const selectedBounds = polygonBounds(selected?.vertices_json);
    if (selectedBounds) {
      mapRef.current.fitBounds(selectedBounds, { padding: [26, 26], animate: false, maxZoom: 15 });
      return;
    }

    const bounds = group.getBounds();
    if (bounds.isValid()) {
      mapRef.current.fitBounds(bounds, { padding: [26, 26], animate: false, maxZoom: 14 });
    }
  }, [onSelect, poligonos, selectedId]);

  useEffect(() => {
    if (!active || !mapRef.current) return undefined;
    const timeoutId = setTimeout(() => {
      try { mapRef.current?.invalidateSize(); } catch (error) {}
    }, 70);
    return () => clearTimeout(timeoutId);
  }, [active, poligonos, selectedId]);

  return <div ref={containerRef} className={`farmacia-sucursales-map farmacia-poligonos-admin__map ${className}`.trim()} aria-label="Mapa de polígonos" />;
}

function PolygonEditor({ value, onChange, active = true, color = '#2563eb', expanded = false, onToggle, panelId = 'farmacia-poligonos-editor-panel' }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const polygonRef = useRef(null);
  const vertexLayerRef = useRef(null);
  const latestVerticesRef = useRef(normalizeVertices(value));
  const onChangeRef = useRef(onChange);
  const addModeRef = useRef(false);
  const suppressNextMapClickRef = useRef(false);
  const hasAutoFitRef = useRef(false);
  const [selectedVertexIndex, setSelectedVertexIndex] = useState(null);
  const [addMode, setAddMode] = useState(false);
  const [lastAddedVertexIndex, setLastAddedVertexIndex] = useState(null);
  const [lastInsertSummary, setLastInsertSummary] = useState('Modo seguro');

  useEffect(() => {
    const vertices = normalizeVertices(value);
    latestVerticesRef.current = vertices;
    setSelectedVertexIndex((current) => (current !== null && current >= vertices.length ? null : current));
    setLastAddedVertexIndex((current) => (current !== null && current >= vertices.length ? null : current));
  }, [value]);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    addModeRef.current = addMode;
  }, [addMode]);

  useEffect(() => {
    if (!expanded) {
      setAddMode(false);
      setLastAddedVertexIndex(null);
      setLastInsertSummary('Modo seguro');
    }
  }, [expanded]);

  const focusEditorMap = useCallback(() => {
    if (!mapRef.current) return;
    const vertices = latestVerticesRef.current;
    const bounds = polygonBounds(vertices);
    if (bounds) {
      mapRef.current.fitBounds(bounds, { padding: [32, 32], animate: true, maxZoom: 16 });
      hasAutoFitRef.current = true;
      return;
    }
    mapRef.current.setView(DEFAULT_CENTER, 11, { animate: true });
    hasAutoFitRef.current = true;
  }, []);

  const focusVertex = useCallback((index) => {
    if (!mapRef.current) return;
    const point = latestVerticesRef.current[index];
    if (!point) return;
    const nextZoom = Math.max(mapRef.current.getZoom(), 16);
    mapRef.current.setView([point.latitud, point.longitud], nextZoom, { animate: true });
  }, []);

  const undoLastVertex = useCallback(() => {
    const vertices = latestVerticesRef.current;
    if (!vertices.length) return;
    const removeIndex = lastAddedVertexIndex !== null && lastAddedVertexIndex < vertices.length
      ? lastAddedVertexIndex
      : vertices.length - 1;
    const next = vertices.filter((_, index) => index !== removeIndex);
    setSelectedVertexIndex(null);
    setLastAddedVertexIndex(null);
    setLastInsertSummary('Modo seguro');
    onChangeRef.current?.(next);
  }, [lastAddedVertexIndex]);

  const requestDeleteVertex = useCallback(async (index) => {
    const vertices = latestVerticesRef.current;
    const point = vertices[index];
    if (!point) return;

    const confirm = await fireConfirmDialog({
      title: `Eliminar vértice ${index + 1}`,
      html: buildCrmSwalBody({
        lead: 'Se quitará este punto del perímetro del polígono.',
        details: [
          { label: 'Punto seleccionado', value: `#${index + 1}` }
        ],
        items: [
          'El perímetro se redibuja apenas confirmás',
          'Podés revisar el resultado antes de guardar los cambios'
        ]
      }),
      icon: 'warning',
      confirmButtonText: 'Eliminar vértice',
      cancelButtonText: 'Mantener',
      width: 'min(430px, 92vw)',
      customClass: {
        container: 'swal-crm-container farmacia-poligonos-admin__confirm-container',
        popup: 'farmacia-poligonos-admin__confirm-popup'
      },
      didOpen: () => {
        const container = Swal.getContainer();
        if (container) container.style.zIndex = '5200';
        const popup = Swal.getPopup();
        if (popup) popup.style.zIndex = '5201';
      }
    });
    if (!confirm.isConfirmed) return;

    const next = vertices.filter((_, pointIndex) => pointIndex !== index);
    setSelectedVertexIndex(null);
    setLastAddedVertexIndex(null);
    setLastInsertSummary('Modo seguro');
    onChangeRef.current?.(next);
    showToast({ title: 'Vértice eliminado', text: `Se quitó el punto #${index + 1} del polígono.` });
  }, []);

  useEffect(() => {
    if (!expanded || !containerRef.current || mapRef.current) return undefined;
    const map = L.map(containerRef.current, {
      center: DEFAULT_CENTER,
      zoom: 11,
      zoomAnimation: false,
      fadeAnimation: false,
      markerZoomAnimation: false
    });
    mapRef.current = map;
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: '' }).addTo(map);
    map.attributionControl.setPrefix('');
    const handleMapClick = (event) => {
      if (suppressNextMapClickRef.current) {
        suppressNextMapClickRef.current = false;
        return;
      }
      if (!addModeRef.current) return;
      const lat = Number(event.latlng?.lat);
      const lng = Number(event.latlng?.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      const inserted = insertVertexOnClosestSegment(latestVerticesRef.current, { latitud: lat, longitud: lng });
      const next = inserted.vertices;
      const insertedIndex = inserted.insertIndex ?? next.length - 1;
      const hasSmartBridge = inserted.beforeIndex !== null && inserted.afterIndex !== null;
      setSelectedVertexIndex(insertedIndex);
      setLastAddedVertexIndex(insertedIndex);
      setLastInsertSummary(hasSmartBridge
        ? `Entre ${inserted.beforeIndex + 1} y ${inserted.afterIndex + 1}`
        : 'Punto agregado');
      addModeRef.current = false;
      setAddMode(false);
      onChangeRef.current?.(next);
    };
    map.on('click', handleMapClick);
    return () => {
      if (mapRef.current) {
        try { mapRef.current.off('click', handleMapClick); } catch (error) {}
        try { mapRef.current.remove(); } catch (error) {}
        mapRef.current = null;
      }
      polygonRef.current = null;
      vertexLayerRef.current = null;
      hasAutoFitRef.current = false;
    };
  }, [expanded]);

  useEffect(() => {
    if (!expanded || !mapRef.current) return;
    const map = mapRef.current;

    if (polygonRef.current) {
      try { polygonRef.current.remove(); } catch (error) {}
      polygonRef.current = null;
    }
    if (vertexLayerRef.current) {
      try { vertexLayerRef.current.remove(); } catch (error) {}
      vertexLayerRef.current = null;
    }
    const vertices = normalizeVertices(value);

    if (vertices.length) {
      const layer = L.featureGroup();
      vertices.forEach((point, index) => {
        const isSelected = selectedVertexIndex === index;
        const marker = L.marker([point.latitud, point.longitud], {
          draggable: true,
          riseOnHover: true,
          bubblingMouseEvents: false,
          icon: L.divIcon({
            className: `farmacia-poligonos-admin__vertex-pin${isSelected ? ' is-selected' : ''}`,
            html: `<span>${index + 1}</span>`,
            iconSize: [28, 28],
            iconAnchor: [14, 14]
          })
        });
        marker.on('click', (event) => {
          suppressNextMapClickRef.current = true;
          try { L.DomEvent.stopPropagation(event.originalEvent); } catch (error) {}
          setSelectedVertexIndex(index);
          window.setTimeout(() => {
            suppressNextMapClickRef.current = false;
          }, 120);
        });
        marker.on('mousedown', () => {
          suppressNextMapClickRef.current = true;
          window.setTimeout(() => {
            suppressNextMapClickRef.current = false;
          }, 120);
        });
        marker.on('dragend', () => {
          suppressNextMapClickRef.current = true;
          const position = marker.getLatLng();
          const currentVertices = latestVerticesRef.current.length === vertices.length ? latestVerticesRef.current : vertices;
          const next = currentVertices.map((item, itemIndex) => itemIndex === index
            ? { latitud: Number(position.lat.toFixed(7)), longitud: Number(position.lng.toFixed(7)) }
            : item);
          onChangeRef.current?.(next);
          window.setTimeout(() => {
            suppressNextMapClickRef.current = false;
          }, 120);
        });
        marker.addTo(layer);
      });
      if (vertices.length >= 2) {
        L.polyline(vertices.map((point) => [point.latitud, point.longitud]), {
          color: normalizeColor(color),
          weight: 2.5,
          opacity: 0.85,
          dashArray: vertices.length >= 3 ? undefined : '8 6'
        }).addTo(layer);
      }
      if (vertices.length >= 3) {
        polygonRef.current = L.polygon(vertices.map((point) => [point.latitud, point.longitud]), {
          color: normalizeColor(color),
          weight: 3,
          fillColor: POLYGON_FILL_COLOR,
          fillOpacity: 0.18
        }).addTo(layer);
      }
      layer.addTo(map);
      vertexLayerRef.current = layer;

      const bounds = polygonBounds(vertices);
      if (bounds && !hasAutoFitRef.current) {
        map.fitBounds(bounds, { padding: [26, 26], animate: false, maxZoom: 16 });
        hasAutoFitRef.current = true;
      }
    } else {
      if (!hasAutoFitRef.current) {
        map.setView(DEFAULT_CENTER, 11, { animate: false });
        hasAutoFitRef.current = true;
      }
    }
  }, [color, expanded, selectedVertexIndex, value]);

  useEffect(() => {
    if (!active || !expanded || !mapRef.current) return undefined;
    const timeoutId = setTimeout(() => {
      try { mapRef.current?.invalidateSize(); } catch (error) {}
    }, 60);
    return () => clearTimeout(timeoutId);
  }, [active, expanded]);

  const vertices = normalizeVertices(value);
  const selectedVertex = selectedVertexIndex !== null ? vertices[selectedVertexIndex] : null;
  const sequenceLabel = vertices.length >= 3 ? 'Perímetro listo' : `${Math.max(3 - vertices.length, 0)} puntos para cerrar`;
  const addButtonLabel = addMode ? 'Cancelar punto' : 'Agregar punto';

  return (
    <div className="farmacia-sucursales-picker farmacia-poligonos-admin__editor">
      <button
        type="button"
        className={`farmacia-poligonos-admin__editor-toggle${expanded ? ' is-open' : ''}`}
        onClick={() => onToggle?.()}
        aria-expanded={expanded ? 'true' : 'false'}
        aria-controls={panelId}
      >
        <div className="farmacia-poligonos-admin__editor-toggle-copy">
          <span>Editor del polígono</span>
          {/* <small>
            {expanded
              ? 'Activá agregar punto para sumar uno nuevo. Arrastrá los badges para afinar el perímetro.'
              : 'Mostrá el editor visual para revisar, ajustar o sumar puntos al perímetro.'}
          </small> */}
        </div>
        <div className="farmacia-poligonos-admin__editor-toggle-meta">
          <strong>{normalizeVertices(value).length}</strong>
          <span>vértices</span>
          <span className={`farmacia-poligonos-admin__editor-toggle-arrow${expanded ? ' is-open' : ''}`} aria-hidden="true" />
        </div>
      </button>

      {expanded ? (
        <div id={panelId} className="farmacia-poligonos-admin__editor-panel">
          <div className="farmacia-poligonos-admin__draw-assistant" style={{ '--polygon-color': normalizeColor(color) }}>
            <div className="farmacia-poligonos-admin__draw-assistant-main">
              {/* <div className={`farmacia-poligonos-admin__draw-next${addMode ? ' is-armed' : ''}`}>
                <span>+</span>
              </div> */}
              <div>
                {/* <span className="farmacia-poligonos-admin__draw-kicker">{addMode ? 'Inserción inteligente activa' : 'Agregar punto inteligente'}</span> */}
                {/* <strong>{addMode ? 'Tocá el mapa y lo uno al tramo más cercano' : 'Se conecta a los dos puntos cercanos'}</strong> */}
                {/* <small>
                  {addMode
                    ? 'No baja desde el último badge: queda insertado entre los dos puntos del tramo más cercano.'
                    : 'Primero activalo, después tocá el mapa. Evitamos líneas largas y puntos accidentales.'}
                </small> */}
              </div>
            </div>
            <div className="farmacia-poligonos-admin__draw-status">
              {/* <span>{sequenceLabel}</span> */}
              {/* <strong>{lastInsertSummary}</strong> */}
              <button
                type="button"
                className={addMode ? 'is-armed' : ''}
                onClick={() => setAddMode((current) => {
                  const next = !current;
                  addModeRef.current = next;
                  return next;
                })}
              >
                <UiIcon name="plus" />
                <span>{addButtonLabel}</span>
              </button>
              <button type="button" className="is-danger-soft" onClick={undoLastVertex} disabled={!vertices.length}>
                <UiIcon name="trash" />
                <span>Deshacer último</span>
              </button>
              <button type="button" onClick={focusEditorMap} disabled={!vertices.length}>
                <UiIcon name="refresh" />
                <span>Centrar mapa</span>
              </button>
            </div>
          </div>

          {selectedVertex ? (
            <div className="farmacia-poligonos-admin__vertex-spotlight" style={{ '--polygon-color': normalizeColor(color) }}>
              <div className="farmacia-poligonos-admin__vertex-spotlight-orbit" aria-hidden="true" />
              <div className="farmacia-poligonos-admin__vertex-spotlight-head">
                <div className="farmacia-poligonos-admin__vertex-spotlight-badge">{selectedVertexIndex + 1}</div>
                <div>
                  <span>Vértice seleccionado</span>
                  <strong>Punto #{selectedVertexIndex + 1}</strong>
                </div>
              </div>
              <div className="farmacia-poligonos-admin__vertex-spotlight-actions">
                <button type="button" onClick={() => focusVertex(selectedVertexIndex)}>
                  <UiIcon name="refresh" />
                  <span>Centrar punto</span>
                </button>
                <button type="button" className="is-danger" onClick={() => void requestDeleteVertex(selectedVertexIndex)}>
                  <UiIcon name="trash" />
                  <span>Eliminar</span>
                </button>
              </div>
            </div>
          ) : null}

          <div
            ref={containerRef}
            className={`farmacia-sucursales-picker__map farmacia-poligonos-admin__editor-map${addMode ? ' is-add-mode' : ''}`}
            aria-label="Editor de polígono"
          />
        </div>
      ) : null}
    </div>
  );
}

function PolygonStatusToggle({ value, onChange }) {
  const isActive = Number(value) !== 0;

  return (
    <button
      type="button"
      className={`farmacia-sucursales-admin__cadeteria-toggle farmacia-poligonos-admin__status-toggle${isActive ? ' is-on' : ''}`}
      onClick={() => onChange?.(isActive ? 0 : 1)}
      aria-pressed={isActive ? 'true' : 'false'}
      aria-label={isActive ? 'Desactivar polígono' : 'Activar polígono'}
    >
      <span className="farmacia-sucursales-admin__cadeteria-track">
        <span className="farmacia-sucursales-admin__cadeteria-thumb" />
      </span>
      <span className="farmacia-sucursales-admin__cadeteria-copy farmacia-poligonos-admin__status-copy">
        <strong>{isActive ? 'Activo en ruteo' : 'Guardado inactivo'}</strong>
        <small>
          {isActive
            ? 'Si guardás este polígono, reemplaza al activo actual en la misma operación.'
            : 'Queda guardado sin intervenir en el ruteador hasta que lo actives.'}
        </small>
      </span>
    </button>
  );
}

function PolygonColorField({ value, onChange }) {
  const normalized = normalizeColor(value);

  return (
    <div className="farmacia-poligonos-admin__color-field">
      <div className="farmacia-poligonos-admin__color-current">
        <span className="farmacia-poligonos-admin__color-preview" style={{ '--polygon-color': normalized }} />
        <input type="color" value={normalized} onChange={(e) => onChange?.(e.target.value)} aria-label="Elegir color del polígono" />
      </div>
      {/* <div className="farmacia-poligonos-admin__color-presets" role="list" aria-label="Colores sugeridos">
        {DEFAULT_COLORS.map((color) => {
          const selected = normalizeColor(color) === normalized;
          return (
            <button
              key={color}
              type="button"
              className={`farmacia-poligonos-admin__color-preset${selected ? ' is-selected' : ''}`}
              style={{ '--polygon-color': color }}
              onClick={() => onChange?.(color)}
              aria-pressed={selected ? 'true' : 'false'}
              aria-label={`Usar color ${color}`}
            />
          );
        })}
      </div> */}
    </div>
  );
}

export default function FarmaciaPoligonosAdmin() {
  const [poligonos, setPoligonos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [selectedId, setSelectedId] = useState('');
  const [createDrawerOpen, setCreateDrawerOpen] = useState(false);
  const [mapPopupOpen, setMapPopupOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyFormState());
  const [createAccordion, setCreateAccordion] = useState(null);
  const [creating, setCreating] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState(emptyFormState());
  const [editAccordion, setEditAccordion] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [deletingId, setDeletingId] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const poligonosRes = await fetch(`${API_BASE_URL}${ENDPOINTS.list}`, { credentials: 'include' });
      const poligonosJson = await poligonosRes.json().catch(() => ({}));
      if (!poligonosRes.ok) throw new Error(poligonosJson?.message || poligonosJson?.error || `HTTP ${poligonosRes.status}`);
      const nextPoligonos = sortPoligonos(Array.isArray(poligonosJson?.poligonos) ? poligonosJson.poligonos : []);
      setPoligonos(nextPoligonos);
      setCreateForm(emptyFormState());
      setSelectedId((current) => current || String(nextPoligonos[0]?.id || ''));
    } catch (err) {
      setError(err.message || 'No se pudo cargar la configuración de polígonos.');
      setPoligonos([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const filteredPoligonos = useMemo(() => {
    const q = normalizeText(search);
    if (!q) return poligonos;
    return poligonos.filter((item) => {
      const bag = [
        item?.nombre,
        item?.notas,
        polygonStatusText(item?.activo)
      ];
      return bag.some((value) => normalizeText(value).includes(q));
    });
  }, [poligonos, search]);

  const totalPages = useMemo(() => {
    if (pageSize <= 0) return 1;
    return Math.max(1, Math.ceil(filteredPoligonos.length / pageSize));
  }, [filteredPoligonos.length, pageSize]);

  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  const pageItems = useMemo(() => {
    if (pageSize <= 0) return filteredPoligonos;
    const start = (page - 1) * pageSize;
    return filteredPoligonos.slice(start, start + pageSize);
  }, [filteredPoligonos, page, pageSize]);

  const selectedPoligono = useMemo(
    () => poligonos.find((item) => String(item?.id) === String(selectedId || '')) || filteredPoligonos[0] || null,
    [filteredPoligonos, poligonos, selectedId]
  );

  useEffect(() => {
    if (!selectedPoligono && poligonos[0]) {
      setSelectedId(String(poligonos[0].id));
    }
  }, [poligonos, selectedPoligono]);

  const activosCount = useMemo(
    () => poligonos.filter((item) => Number(item?.activo) !== 0).length,
    [poligonos]
  );

  const verticesCount = useMemo(
    () => poligonos.reduce((acc, item) => acc + normalizeVertices(item?.vertices_json).length, 0),
    [poligonos]
  );

  const selectedVerticesCount = useMemo(
    () => normalizeVertices(selectedPoligono?.vertices_json).length,
    [selectedPoligono]
  );

  const rangeStart = filteredPoligonos.length === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, filteredPoligonos.length);

  function updateCreateField(field, value) {
    setCreateForm((current) => ({ ...current, [field]: value }));
  }

  function updateEditField(field, value) {
    setEditForm((current) => ({ ...current, [field]: value }));
  }

  const closeCreateDrawer = useCallback(() => {
    if (creating) return;
    setCreateDrawerOpen(false);
    setCreateAccordion(null);
  }, [creating]);

  function openEdit(row) {
    setEditRow(row);
    setEditForm(formStateFromRow(row));
    setEditAccordion('editor');
  }

  const closeEdit = useCallback(() => {
    if (editSaving) return;
    setEditRow(null);
    setEditForm(emptyFormState());
    setEditAccordion(null);
  }, [editSaving]);

  useEffect(() => {
    if (!createDrawerOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !creating) closeCreateDrawer();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [closeCreateDrawer, createDrawerOpen, creating]);

  useEffect(() => {
    if (!editRow) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !editSaving) closeEdit();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [closeEdit, editRow, editSaving]);

  useEffect(() => {
    if (!mapPopupOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setMapPopupOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [mapPopupOpen]);

  async function handleCreate(event) {
    event.preventDefault();
    const payload = payloadFromForm(createForm);
    if (!payload.nombre || payload.vertices_json.length < 3) return;
    setCreating(true);
    try {
      const res = await fetch(`${API_BASE_URL}${ENDPOINTS.create}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || json?.error || `HTTP ${res.status}`);
      const created = json?.poligono;
      const next = sortPoligonos(created ? [...poligonos, created] : poligonos);
      setPoligonos(next);
      setSelectedId(String(created?.id || selectedId || ''));
      setCreateForm(emptyFormState());
      setCreateDrawerOpen(false);
      showToast({ title: 'Polígono creado', text: created?.nombre || 'La zona quedó lista para operar.' });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'No se pudo crear', text: err.message || 'Error al crear el polígono.' });
    } finally {
      setCreating(false);
    }
  }

  async function submitEdit(event) {
    event.preventDefault();
    if (!editRow || editSaving) return;
    const payload = payloadFromForm(editForm);
    if (!payload.nombre || payload.vertices_json.length < 3) return;
    setEditSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}${ENDPOINTS.update(editRow.id)}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || json?.error || `HTTP ${res.status}`);
      const updated = json?.poligono;
      const next = sortPoligonos(poligonos.map((item) => (Number(item?.id) === Number(editRow?.id) ? updated : item)));
      setPoligonos(next);
      setSelectedId(String(updated?.id || editRow?.id));
      closeEdit();
      showToast({ title: 'Polígono actualizado', text: updated?.nombre || 'Se guardaron los cambios.' });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'No se pudo actualizar', text: err.message || 'Error al actualizar el polígono.' });
    } finally {
      setEditSaving(false);
    }
  }

  async function requestDelete(row) {
    const confirm = await fireConfirmDialog({
      title: 'Eliminar polígono',
      html: buildCrmSwalBody({
        lead: 'Se quitará esta cobertura del ruteador de Farmacia.',
        details: [
          { label: 'Polígono', value: row?.nombre || 'Sin nombre' },
          { label: 'Is Active', value: Number(row?.activo) !== 0 ? 'Sí' : 'No' }
        ],
        items: [
          'Esta acción no se puede deshacer',
          'Si era el único activo, después podés activar otro desde el listado'
        ]
      }),
      icon: 'warning',
      confirmButtonText: 'Eliminar',
      cancelButtonText: 'Cancelar',
      width: 'min(420px, 92vw)'
    });
    if (!confirm.isConfirmed) return;

    setDeletingId(String(row?.id || ''));
    try {
      const res = await fetch(`${API_BASE_URL}${ENDPOINTS.delete(row.id)}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || json?.error || `HTTP ${res.status}`);
      const next = poligonos.filter((item) => Number(item?.id) !== Number(row?.id));
      setPoligonos(next);
      if (String(selectedId) === String(row?.id)) {
        setSelectedId(String(next[0]?.id || ''));
      }
      showToast({ title: 'Polígono eliminado', text: row?.nombre || 'La cobertura se eliminó correctamente.' });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'No se pudo eliminar', text: err.message || 'Error al eliminar el polígono.' });
    } finally {
      setDeletingId('');
    }
  }

  return (
    <section className="farmacia-sucursales-admin farmacia-poligonos-admin crm-gd-page crm-pl-page crm-pbody">
      <div className="farmacia-sucursales-admin__shell">
        <div className="farmacia-sucursales-admin__top">
          <header className="farmacia-sucursales-admin__hero">
            <div>
              <span className="farmacia-sucursales-admin__eyebrow">Farmacia · Ruteador de cobertura</span>
            </div>
            {/* <button
              type="button"
              className="farmacia-sucursales-admin__primary farmacia-sucursales-admin__hero-action"
              onClick={() => {
                setCreateDrawerOpen(true);
                setCreateAccordion('editor');
              }}
            >
              <UiIcon name="plus" />
              <span>Agregar polígono</span>
            </button> */}
          </header>

          <div className="farmacia-sucursales-admin__stats farmacia-poligonos-admin__stats-grid">
            <article>
              <span>Total</span>
              <strong>{poligonos.length}</strong>
              <small>Zonas cargadas en el ruteador</small>
            </article>
            <article>
              <span>Activos</span>
              <strong>{activosCount}</strong>
              <small>Perímetros listos para operar</small>
            </article>
            <article>
              <span>Vértices</span>
              <strong>{verticesCount}</strong>
              <small>Puntos actualmente definidos</small>
            </article>
            <article>
              <span>Foco actual</span>
              <strong>{selectedPoligono?.nombre || 'Sin selección'}</strong>
              <small>{selectedPoligono ? `${selectedVerticesCount} vértices en revisión` : 'Elegí una zona para revisar su ficha'}</small>
            </article>
          </div>
        </div>

        <div className="farmacia-sucursales-admin__scroll">
          <div className="farmacia-sucursales-admin__grid">
            <div className="farmacia-sucursales-admin__main">
              <div className="farmacia-sucursales-admin__panel">
                <div className="farmacia-sucursales-admin__toolbar">
                  <div>
                    <h2>Listado</h2>
                    <p>Buscá por nombre, estado operativo o notas internas.</p>
                  </div>
                  <div className="farmacia-sucursales-admin__toolbar-actions">
                    <div className="farmacia-sucursales-admin__search-wrap">
                      <CwListSearch value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar polígono…" />
                    </div>
                    <label className="farmacia-sucursales-admin__page-size">
                      <span>Ver</span>
                      <select value={pageSize} onChange={(e) => setPage(Number(e.target.value) || 10)}>
                        {PAGE_SIZE_OPTIONS.map((option) => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    </label>
                  </div>
                </div>

                {error ? (
                  <div className="farmacia-sucursales-admin__error">
                    <UiIcon name="alert" />
                    <span>{error}</span>
                  </div>
                ) : null}

                <div className="farmacia-sucursales-admin__table-wrap">
                  <table className="farmacia-sucursales-admin__table farmacia-poligonos-admin__table">
                    <thead>
                      <tr>
                        <th>Polígono</th>
                        <th>Cobertura</th>
                        <th>Estado</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={4} className="farmacia-sucursales-admin__empty">
                            <div className="farmacia-sucursales-admin__loading-state farmacia-poligonos-admin__loading-state" role="status" aria-live="polite">
                              <div className="farmacia-sucursales-admin__loading-badge">
                                <span className="farmacia-sucursales-admin__loading-spinner" aria-hidden="true" />
                                <strong>Sincronizando polígonos</strong>
                              </div>
                              <span>Estamos preparando el tablero de cobertura y enfocando las zonas operativas.</span>
                              <div className="farmacia-sucursales-admin__loading-skeleton" aria-hidden="true">
                                <span />
                                <span />
                                <span />
                              </div>
                            </div>
                          </td>
                        </tr>
                      ) : pageItems.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="farmacia-sucursales-admin__empty">
                            <div className="farmacia-sucursales-admin__empty-state" role="status" aria-live="polite">
                              <div className="farmacia-sucursales-admin__empty-icon" aria-hidden="true">
                                <span className="farmacia-sucursales-admin__empty-icon-ring" />
                                <UiIcon name="search" />
                              </div>
                              <div className="farmacia-sucursales-admin__empty-copy">
                                <strong>No hay polígonos para mostrar</strong>
                                <span>
                                  {String(search || '').trim()
                                    ? 'Probá con otra búsqueda para encontrar una zona ya cargada.'
                                    : 'Cuando dibujes la primera cobertura operativa, va a aparecer en este listado.'}
                                </span>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ) : pageItems.map((row) => {
                        const isSelected = String(selectedId) === String(row?.id);
                        const vertexCount = normalizeVertices(row?.vertices_json).length;
                        return (
                          <tr
                            key={row.id}
                            className={isSelected ? 'is-selected' : ''}
                            onClick={() => setSelectedId(String(row.id))}
                            aria-selected={isSelected ? 'true' : 'false'}
                          >
                            <td>
                              <div className="farmacia-sucursales-admin__branch-main">
                                <div className="farmacia-sucursales-admin__branch-title-row">
                                  <strong>{row.nombre}</strong>
                                  <span className="farmacia-poligonos-admin__color-chip" style={{ '--polygon-color': normalizeColor(row.color) }} />
                                </div>
                                <span className="farmacia-sucursales-admin__branch-subtitle farmacia-poligonos-admin__branch-subtitle">
                                  Prioridad {Number(row?.prioridad || 0)} · {row?.notas ? 'Con notas operativas' : 'Sin notas operativas'}
                                </span>
                              </div>
                            </td>
                            <td>
                              <div className="farmacia-sucursales-admin__geo-cell farmacia-poligonos-admin__coverage-cell">
                                <span className="farmacia-sucursales-admin__geo-badge is-ready">{vertexCount} pts</span>
                                <small>{verticesToPreview(row?.vertices_json)}</small>
                              </div>
                            </td>
                            <td>
                              <div className="farmacia-sucursales-admin__geo-cell">
                                <span className={`farmacia-sucursales-admin__geo-badge${Number(row?.activo) !== 0 ? ' is-ready' : ''}`}>
                                  {polygonStatusText(row?.activo)}
                                </span>
                                <small>{Number(row?.activo) !== 0 ? 'Disponible para enrutar' : 'Fuera de operación'}</small>
                              </div>
                            </td>
                            <td>
                              <div className="farmacia-sucursales-admin__actions">
                                <button
                                  type="button"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    openEdit(row);
                                  }}
                                  aria-label={`Editar ${row.nombre}`}
                                >
                                  <UiIcon name="edit" />
                                </button>
                                {/* <button
                                  type="button"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    void requestDelete(row);
                                  }}
                                  disabled={deletingId === String(row.id)}
                                  aria-label={`Eliminar ${row.nombre}`}
                                >
                                  <UiIcon name="trash" />
                                </button> */}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <AmvTablePager
                  rangeStart={rangeStart}
                  rangeEnd={rangeEnd}
                  filteredCount={filteredPoligonos.length}
                  totalCount={poligonos.length}
                  searchActive={Boolean(String(search || '').trim())}
                  page={page}
                  totalPages={totalPages}
                  pageSize={pageSize}
                  onPageChange={setPage}
                  entityLabel="polígonos"
                />
              </div>
            </div>

            <aside className="farmacia-sucursales-admin__side">
              <div className="farmacia-sucursales-admin__panel farmacia-sucursales-admin__panel--map">
                <div className="farmacia-sucursales-admin__panel-head farmacia-sucursales-admin__panel-head--side" style={{marginBottom: '0.5rem'}}>
                  <div>
                    <span className="farmacia-poligonos-admin__panel-kicker">Mapa operativo</span>
                  </div>
                  <button
                    type="button"
                    className="farmacia-poligonos-admin__map-popout-button"
                    onClick={() => setMapPopupOpen(true)}
                  >
                    Ver mapa
                  </button>
                </div>
                <button
                  type="button"
                  className="farmacia-poligonos-admin__map-launcher"
                  onClick={() => setMapPopupOpen(true)}
                  aria-label="Abrir mapa operativo en popup"
                >
                  <div className="farmacia-poligonos-admin__map-launcher-frame">
                    <PolygonMap poligonos={filteredPoligonos} selectedId={selectedPoligono?.id} onSelect={setSelectedId} />
                  </div>
                  <div className="farmacia-poligonos-admin__map-launcher-overlay">
                    <span className="farmacia-poligonos-admin__map-launcher-kicker">Vista inmersiva</span>
                    <strong>{selectedPoligono?.nombre || 'Mapa de cobertura'}</strong>
                    <small>Click para abrir el mapa grande y navegar la zona con más detalle.</small>
                  </div>
                </button>
              </div>

              <div className="farmacia-sucursales-admin__panel farmacia-sucursales-admin__panel--detail">
                <div className="farmacia-sucursales-admin__panel-head farmacia-sucursales-admin__panel-head--side">
                </div>
                {selectedPoligono ? (
                  <div className="farmacia-sucursales-admin__detail-card farmacia-poligonos-admin__detail-card farmacia-poligonos-admin__detail-card--premium">
                    <div className="farmacia-sucursales-admin__detail-title">
                      <span className="farmacia-sucursales-admin__detail-kicker">Cobertura seleccionada</span>
                      <strong>{selectedPoligono.nombre}</strong>
                      <p>{Number(selectedPoligono?.activo) !== 0 ? 'Polígono activo actual' : 'Polígono inactivo'}</p>
                    </div>
                    <dl>
                      <div><dt>Estado</dt><dd>{polygonStatusText(selectedPoligono?.activo)}</dd></div>
                      <div><dt>Prioridad</dt><dd>{selectedPoligono?.prioridad ?? 0}</dd></div>
                      <div><dt>Color</dt><dd>{selectedPoligono?.color || 'Sin dato'}</dd></div>
                      <div><dt>Vértices</dt><dd>{selectedVerticesCount}</dd></div>
                      <div className="is-wide"><dt>Notas</dt><dd>{selectedPoligono?.notas || 'Sin notas operativas'}</dd></div>
                    </dl>
                  </div>
                ) : (
                  <div className="farmacia-sucursales-admin__empty-card farmacia-poligonos-admin__empty-card">
                    Elegí una cobertura del listado para ver su ficha operativa, prioridad y notas en este panel.
                  </div>
                )}
              </div>
            </aside>
          </div>
        </div>
      </div>

      <div
        className={`crm-gd-drawer-backdrop crm-gd-drawer-backdrop--left farmacia-sucursales-admin__drawer-backdrop${
          createDrawerOpen ? ' crm-gd-drawer-backdrop--open' : ' crm-gd-drawer-backdrop--closed'
        }`}
        role="presentation"
        aria-hidden={!createDrawerOpen}
      >
        <aside
          className={`crm-gd-drawer-panel farmacia-sucursales-admin__drawer${
            createDrawerOpen ? ' crm-gd-drawer-panel--left-open' : ' crm-gd-drawer-panel--left-closed'
          }`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="farmacia-poligonos-create-title"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="farmacia-sucursales-admin__drawer-stripe" aria-hidden="true" />
          <div className="farmacia-sucursales-admin__drawer-head">
            <div>
              <span className="farmacia-sucursales-admin__drawer-eyebrow">Alta rápida</span>
              <h2 id="farmacia-poligonos-create-title">Agregar polígono</h2>
              <p>Dibujá la zona, definí prioridad operativa y elegí si este va a ser el único activo.</p>
            </div>
            <button type="button" className="farmacia-sucursales-admin__drawer-close" onClick={closeCreateDrawer} aria-label="Cerrar drawer">
              ×
            </button>
          </div>

          <form className="farmacia-sucursales-admin__drawer-form" onSubmit={handleCreate}>
            <div className="farmacia-sucursales-admin__drawer-body farmacia-poligonos-admin__drawer-body">
              <section className="farmacia-poligonos-admin__form-cluster">
                <label className="farmacia-poligonos-admin__compact-field farmacia-poligonos-admin__compact-field--name">
                  <span>Nombre</span>
                  <input value={createForm.nombre} onChange={(e) => updateCreateField('nombre', e.target.value)} placeholder="Zona Luján Centro" required />
                </label>

                <div className="farmacia-poligonos-admin__meta-grid">
                  <div className="farmacia-poligonos-admin__meta-card farmacia-poligonos-admin__meta-card--color">
                    <span>Color</span>
                    <PolygonColorField value={createForm.color} onChange={(value) => updateCreateField('color', value)} />
                  </div>

                  <label className="farmacia-poligonos-admin__meta-card farmacia-poligonos-admin__meta-card--priority">
                    <span>Prioridad</span>
                    <input type="number" value={createForm.prioridad} onChange={(e) => updateCreateField('prioridad', e.target.value)} placeholder="0" />
                  </label>

                  {/* <div className="farmacia-poligonos-admin__meta-card farmacia-poligonos-admin__meta-card--status">
                    <span>Estado operativo</span>
                    <PolygonStatusToggle value={createForm.activo} onChange={(value) => updateCreateField('activo', value)} />
                  </div> */}
                </div>
              </section>

              <div className="farmacia-sucursales-admin__form-grid farmacia-sucursales-admin__form-grid--drawer farmacia-poligonos-admin__form-grid">
                <label className="is-wide">
                  <span>Notas</span>
                  <textarea value={createForm.notas} onChange={(e) => updateCreateField('notas', e.target.value)} placeholder="Observaciones operativas, avenidas de corte, límites, exclusiones…" rows={3} />
                </label>
                <div className="is-wide farmacia-sucursales-admin__map-field">
                  <PolygonEditor
                    value={createForm.vertices_json}
                    onChange={(vertices) => updateCreateField('vertices_json', vertices)}
                    active={createDrawerOpen}
                    color={createForm.color}
                    expanded={createAccordion === 'editor'}
                    onToggle={() => setCreateAccordion((current) => (current === 'editor' ? null : 'editor'))}
                    panelId="farmacia-poligonos-create-editor-panel"
                  />
                </div>
              </div>
            </div>

            <div className="farmacia-sucursales-admin__drawer-actions">
              <button type="button" className="farmacia-sucursales-admin__ghost" onClick={closeCreateDrawer} disabled={creating}>Cancelar</button>
              <button type="submit" className="farmacia-sucursales-admin__primary" disabled={creating || createForm.vertices_json.length < 3}>
                <UiIcon name="plus" />
                <span>{creating ? 'Guardando…' : 'Agregar polígono'}</span>
              </button>
            </div>
          </form>
        </aside>
      </div>

      <div
        className={`crm-gd-drawer-backdrop crm-gd-drawer-backdrop--right farmacia-sucursales-admin__drawer-backdrop${
          editRow ? ' crm-gd-drawer-backdrop--open' : ' crm-gd-drawer-backdrop--closed'
        }`}
        role="presentation"
        aria-hidden={!editRow}
      >
        <aside
          className={`crm-gd-drawer-panel farmacia-sucursales-admin__drawer farmacia-sucursales-admin__drawer--edit${
            editRow ? ' crm-gd-drawer-panel--right-open' : ' crm-gd-drawer-panel--right-closed'
          }`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="farmacia-poligonos-edit-title"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="farmacia-sucursales-admin__drawer-stripe farmacia-sucursales-admin__drawer-stripe--edit" aria-hidden="true" />
          <div className="farmacia-sucursales-admin__drawer-head">
            <div>
              <span className="farmacia-sucursales-admin__drawer-eyebrow">Editar Polígono</span>
            </div>
            <button type="button" className="farmacia-sucursales-admin__drawer-close" onClick={closeEdit} aria-label="Cerrar drawer">
              ×
            </button>
          </div>
          <form className="farmacia-sucursales-admin__drawer-form" onSubmit={submitEdit}>
            <div className="farmacia-sucursales-admin__drawer-body farmacia-poligonos-admin__drawer-body">
              <section className="farmacia-poligonos-admin__form-cluster">
                <label className="farmacia-poligonos-admin__compact-field farmacia-poligonos-admin__compact-field--name">
                  <span>Nombre</span>
                  <input value={editForm.nombre} onChange={(e) => updateEditField('nombre', e.target.value)} required />
                </label>

                <div className="farmacia-poligonos-admin__meta-grid">
                  <div className="farmacia-poligonos-admin__meta-card farmacia-poligonos-admin__meta-card--color">
                    <span>Color</span>
                    <PolygonColorField value={editForm.color} onChange={(value) => updateEditField('color', value)} />
                  </div>

                  <label className="farmacia-poligonos-admin__meta-card farmacia-poligonos-admin__meta-card--priority">
                    <span>Prioridad</span>
                    <input type="number" value={editForm.prioridad} onChange={(e) => updateEditField('prioridad', e.target.value)} />
                  </label>

                  {/* <div className="farmacia-poligonos-admin__meta-card farmacia-poligonos-admin__meta-card--status">
                    <span>Estado operativo</span>
                    <PolygonStatusToggle value={editForm.activo} onChange={(value) => updateEditField('activo', value)} />
                  </div> */}
                </div>
              </section>

              <div className="farmacia-sucursales-admin__form-grid farmacia-sucursales-admin__form-grid--drawer farmacia-poligonos-admin__form-grid">
                <label className="is-wide">
                  <span>Notas</span>
                  <textarea value={editForm.notas} onChange={(e) => updateEditField('notas', e.target.value)} rows={3} />
                </label>
                <div className="is-wide farmacia-sucursales-admin__map-field">
                  <PolygonEditor
                    value={editForm.vertices_json}
                    onChange={(vertices) => updateEditField('vertices_json', vertices)}
                    active={Boolean(editRow)}
                    color={editForm.color}
                    expanded={editAccordion === 'editor'}
                    onToggle={() => setEditAccordion((current) => (current === 'editor' ? null : 'editor'))}
                    panelId="farmacia-poligonos-edit-editor-panel"
                  />
                </div>
              </div>
            </div>
            <div className="farmacia-sucursales-admin__drawer-actions">
              <button type="button" className="farmacia-sucursales-admin__ghost" onClick={closeEdit} disabled={editSaving}>Cancelar</button>
              <button type="submit" className="farmacia-sucursales-admin__primary" disabled={editSaving || editForm.vertices_json.length < 3}>
                {editSaving ? 'Guardando…' : 'Guardar cambios'}
              </button>
            </div>
          </form>
        </aside>
      </div>

      <div
        className={`farmacia-poligonos-admin__map-modal-backdrop${mapPopupOpen ? ' is-open' : ''}`}
        role="presentation"
        onClick={() => setMapPopupOpen(false)}
        aria-hidden={!mapPopupOpen}
      >
        <aside
          className={`farmacia-poligonos-admin__map-modal${mapPopupOpen ? ' is-open' : ''}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="farmacia-poligonos-map-modal-title"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="farmacia-poligonos-admin__map-modal-head">
            <div>
              <span className="farmacia-poligonos-admin__map-modal-kicker">Mapa operativo expandido</span>
              <h2 id="farmacia-poligonos-map-modal-title">{selectedPoligono?.nombre || 'Cobertura activa'}</h2>
              <p>
                {selectedPoligono
                  ? `${polygonStatusText(selectedPoligono?.activo)} · ${normalizeVertices(selectedPoligono?.vertices_json).length} vértices · prioridad ${selectedPoligono?.prioridad ?? 0}`
                  : 'Explorá la cobertura con más detalle y seleccioná otra zona desde el propio mapa.'}
              </p>
            </div>
            <button
              type="button"
              className="farmacia-poligonos-admin__map-modal-close"
              onClick={() => setMapPopupOpen(false)}
              aria-label="Cerrar popup de mapa"
            >
              ×
            </button>
          </div>

          <div className="farmacia-poligonos-admin__map-modal-body">
            <div className="farmacia-poligonos-admin__map-modal-stage">
              <PolygonMap
                poligonos={filteredPoligonos}
                selectedId={selectedPoligono?.id}
                onSelect={setSelectedId}
                active={mapPopupOpen}
                className="farmacia-poligonos-admin__map-modal-canvas"
              />
            </div>

            <div className="farmacia-poligonos-admin__map-modal-aside">
              <div className="farmacia-poligonos-admin__map-modal-card">
                <span>Polígono enfocado</span>
                <strong>{selectedPoligono?.nombre || 'Sin selección'}</strong>
                <small>{selectedPoligono?.notas || 'Sin notas operativas para esta cobertura.'}</small>
              </div>
              <div className="farmacia-poligonos-admin__map-modal-card">
                <span>Estado</span>
                <strong>{selectedPoligono ? polygonStatusText(selectedPoligono?.activo) : 'Sin dato'}</strong>
                <small>{selectedPoligono?.is_active ? 'Es la cobertura activa actual del sistema.' : 'Esta cobertura está guardada, pero no activa.'}</small>
              </div>
              <div className="farmacia-poligonos-admin__map-modal-card farmacia-poligonos-admin__map-modal-card--accent">
                <span>Navegación</span>
                <strong>Click en cualquier perímetro</strong>
                <small>El popup cambia el foco a la zona elegida y mantiene el contexto sin cerrar.</small>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
}
